<template>
  <div class="questions">
    <div class="questions__container mt-3">
      <div class="row">
        <!-- Question form -->
        <div class="col-lg-6">
          <div class="questions__header d-flex justify-content-between align-items-center">
            <h4>الاسئلة</h4>
            <span class="badge bg-primary" v-if="questions.length > 0">
              {{ questions.length }} سؤال - مجموع {{ totalDegree }} درجة
            </span>
          </div>

          <div class="question mt-3">
            <div class="question__header">
              <h5>سؤال</h5>
              <div class="form-group">
                <select
                  class="form-control form-select"
                  aria-label="نوع السؤال"
                  :id="uid('questionType')"
                  v-model="question.questionType"
                  @change="onQuestionTypeChanged"
                >
                  <option
                    v-for="option in questionTypeOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>
            </div>

            <div class="mt-2">
              <div class="form-group">
                <label :for="uid('questionTitle')">رأس السؤال</label>

                <div class="questionTitle">
                  <!-- Image question -->
                  <Transition name="scale" mode="out-in">
                    <UploadImages
                      v-if="question.questionType == QUESTION_TYPE.image"
                      :key="`${uid('upload')}-${uploadResetKey}`"
                      class="form-control"
                      @changed="handleImages"
                      :max="1"
                      maxError="يمكنك رفع صورة واحدة لكل سوال"
                      fileError="لا يمكن رفع ملفات غير الصور"
                    />
                  </Transition>

                  <!-- Text / essay question stem -->
                  <Transition name="scale" mode="out-in">
                    <div
                      v-if="question.questionType != QUESTION_TYPE.image"
                      class="editor__design"
                    >
                      <input
                        :id="uid('filePicker')"
                        type="file"
                        style="display: none"
                      />
                      <editor
                        :api-key="apiKey"
                        v-model="question.title"
                        :init="editorInit"
                      />
                    </div>
                  </Transition>
                </div>
              </div>
            </div>

            <!-- Marks -->
            <div class="form-group mt-2">
              <label :for="uid('questionDegree')">درجة السؤال</label>
              <input
                type="number"
                class="form-control mt-2"
                :id="uid('questionDegree')"
                min="0.5"
                step="0.5"
                placeholder="درجة السؤال"
                v-model.number="question.degree"
              />
            </div>

            <!-- Essay: model answer -->
            <div class="question__answers" v-if="isEssay(question)">
              <h6>الاجابة النموذجية <small class="text-muted">(اختيارية - تظهر للمدرس عند التصحيح)</small></h6>
              <div class="form-group mt-2">
                <textarea
                  class="form-control"
                  rows="4"
                  :id="uid('modelAnswer')"
                  placeholder="اكتب الاجابة النموذجية التي سيتم التصحيح على أساسها"
                  v-model="question.answers[0].title"
                ></textarea>
              </div>
              <p class="text-muted mb-0">
                <i class="fas fa-info-circle ms-1"></i>
                السؤال المقالي يتم تصحيحه يدوياً من صفحة نتائج الطلاب.
              </p>
            </div>

            <!-- Multiple choice answers -->
            <div class="question__answers" v-else>
              <h6>الاجابات</h6>
              <div class="row mt-3">
                <div
                  class="col-lg-6"
                  v-for="(answer, index) in question.answers"
                  :key="index"
                >
                  <div class="answer">
                    <div class="form-group">
                      <input
                        type="radio"
                        :name="uid('isRight')"
                        :value="index"
                        :id="uid(`isRight-${index}`)"
                        v-model="question.isRight"
                      />
                      <input
                        type="text"
                        :placeholder="answerPlaceholders[index]"
                        class="form-control"
                        v-model="answer.title"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="text-start mt-2 mb-2">
            <button type="button" class="btn btn-primary" @click="addQuestion">
              <i class="fa fa-plus ms-2"></i> إضافة سؤال
            </button>
          </div>
        </div>

        <!-- Questions table -->
        <div class="col-lg-6">
          <div class="adding__content more">
            <div class="table-responsive" v-show="questions.length > 0">
              <Transition name="scale" mode="out-in">
                <table
                  class="table table-bordered sub_table styled-table subject-table table-striped"
                >
                  <thead>
                    <tr>
                      <th class="fit" scope="col">الرقم</th>
                      <th style="width: 30%" scope="col">السؤال</th>
                      <th scope="col">الاجابات</th>
                      <th class="fit" scope="col">الدرجة</th>
                      <th scope="col">التحكم</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in questions" :key="rowKey(row, index)">
                      <th class="text-center" scope="row">{{ index + 1 }}</th>
                      <td>
                        <img
                          v-if="row.questionType == QUESTION_TYPE.image"
                          class="w-100"
                          :src="row.url"
                          alt="QuestionImage"
                        />
                        <span v-else v-html="row.title"></span>
                      </td>
                      <td>
                        <template v-if="isEssay(row)">
                          <span class="badge bg-info">مقالي</span>
                          <p class="mb-0 mt-1" v-if="modelAnswerOf(row)">
                            {{ modelAnswerOf(row) }}
                          </p>
                        </template>
                        <ul class="pe-0" v-else>
                          <li
                            v-for="(answer, answerIndex) in row.answers"
                            :key="answerIndex"
                            :class="{ red: row.isRight == answerIndex }"
                          >
                            {{ answerIndex + 1 }}- {{ answer.title }}
                          </li>
                        </ul>
                      </td>
                      <td class="text-center">{{ degreeOf(row) }}</td>
                      <td class="controls" style="width: 22%">
                        <button
                          class="btn edit icon"
                          type="button"
                          data-bs-toggle="modal"
                          :data-bs-target="`#${uid('editQuestionModal')}`"
                          @click="openEdit(index)"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          class="btn delete icon"
                          type="button"
                          @click="deleteQuestion(row, index)"
                        >
                          <i class="fas fa-trash-alt"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Transition>
            </div>

            <div v-show="questions.length == 0">
              <Transition name="scale" mode="out-in">
                <div class="no-data mt-0">
                  <i class="icon fas fa-exclamation-circle"></i>
                  <p>قم بأضافة أسالة للاختبار</p>
                </div>
              </Transition>
            </div>
          </div>

          <slot name="aside"></slot>
        </div>
      </div>
    </div>

    <!-- Edit question modal: one instance, outside the table rows -->
    <div
      class="modal fade"
      :id="uid('editQuestionModal')"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabindex="-1"
      aria-hidden="true"
      ref="editModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="text-start">
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                @click="cancelEdit"
              ></button>
            </div>

            <div class="question mt-3" v-if="editingQuestion">
              <div class="question__header">
                <h5>تعديل السؤال</h5>
                <div class="form-group">
                  <select
                    class="form-control form-select"
                    aria-label="نوع السؤال"
                    v-model="editingQuestion.questionType"
                    @change="onEditingTypeChanged"
                  >
                    <option
                      v-for="option in questionTypeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="mt-2">
                <div class="form-group">
                  <label>رأس السؤال</label>
                  <div class="questionTitle">
                    <Transition name="scale" mode="out-in">
                      <UploadImages
                        v-if="editingQuestion.questionType == QUESTION_TYPE.image"
                        :key="`${uid('editUpload')}-${editUploadResetKey}`"
                        class="form-control"
                        @changed="handleEditImages"
                        :max="1"
                        maxError="يمكنك رفع صورة واحدة لكل سوال"
                        fileError="لا يمكن رفع ملفات غير الصور"
                      />
                    </Transition>

                    <Transition name="scale" mode="out-in">
                      <div
                        v-if="editingQuestion.questionType != QUESTION_TYPE.image"
                        class="editor__design"
                      >
                        <editor
                          :api-key="apiKey"
                          v-model="editingQuestion.title"
                          :init="editorInit"
                        />
                      </div>
                    </Transition>
                  </div>
                </div>
              </div>

              <div class="form-group mt-2">
                <label>درجة السؤال</label>
                <input
                  type="number"
                  class="form-control mt-2"
                  min="0.5"
                  step="0.5"
                  v-model.number="editingQuestion.degree"
                />
              </div>

              <div class="question__answers" v-if="isEssay(editingQuestion)">
                <h6>الاجابة النموذجية</h6>
                <div class="form-group mt-2">
                  <textarea
                    class="form-control"
                    rows="4"
                    v-model="editingQuestion.answers[0].title"
                  ></textarea>
                </div>
              </div>

              <div class="question__answers" v-else>
                <h6>الاجابات</h6>
                <div class="row mt-3">
                  <div
                    class="col-lg-6"
                    v-for="(answer, index) in editingQuestion.answers"
                    :key="index"
                  >
                    <div class="answer">
                      <div class="form-group">
                        <input
                          type="radio"
                          :name="uid('editIsRight')"
                          :value="index"
                          v-model="editingQuestion.isRight"
                        />
                        <input
                          type="text"
                          :placeholder="answerPlaceholders[index]"
                          class="form-control"
                          v-model="answer.title"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group text-start mt-3">
              <button type="button" class="btn btn-primary" @click="saveEdit">
                حفظ
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UploadImages from "vue-upload-drop-images";
import Editor from "@tinymce/tinymce-vue";
import Swal from "sweetalert2";
import { useToast, TYPE } from "vue-toastification";

import FirebaseUploadingFileVue from "@/components/FirebaseUploadingFile.vue";
import { deleteFileFromUrl } from "@/firebase/firebase_storage";
import {
  QUESTION_TYPE,
  QUESTION_TYPE_OPTIONS,
  blankQuestion,
  blankEssayQuestion,
  cloneQuestion,
  isEssayQuestion,
  questionsTotalDegree,
  validateQuestion,
  DEFAULT_QUESTION_DEGREE,
} from "@/constants/exam";

const toast = useToast();

// Instance counter so two editors on one page never share a DOM id.
let instanceCounter = 0;

export default {
  name: "ExamQuestionsEditor",
  components: { Editor, UploadImages },
  props: {
    // v-model:questions — the exam's questions array
    questions: {
      type: Array,
      required: true,
    },
  },
  emits: ["update:questions", "change"],
  data() {
    instanceCounter += 1;
    return {
      instanceId: instanceCounter,
      QUESTION_TYPE,
      questionTypeOptions: QUESTION_TYPE_OPTIONS,
      answerPlaceholders: [
        "الاجابة الاولى",
        "الاجابة الثانية",
        "الاجابة الثالثة",
        "الاجابة الرابعة",
      ],
      apiKey: "f536kduwtjx4cdnvdlyreiw732soeeloiv4zu436uat6t9u7",
      file: null,
      editFile: null,
      // Bumping these keys remounts UploadImages, which is how the image
      // input is cleared — never via document.getElementById.
      uploadResetKey: 0,
      editUploadResetKey: 0,
      question: blankQuestion(),
      editingQuestion: null,
      editingIndex: -1,
      editorInstance: null,
    };
  },
  computed: {
    totalDegree() {
      return questionsTotalDegree(this.questions);
    },
    editorInit() {
      const filePickerId = this.uid("filePicker");
      return {
        height: 300,
        selector: "textarea",
        file_picker_type: true,
        image_title: true,
        automatic_uploads: true,
        file_picker_types: "image",
        file_picker_callback: (callback, value, meta) => {
          if (meta.filetype !== "image") return;
          const input = document.getElementById(filePickerId);
          if (!input) return;
          input.click();
          input.onchange = () => {
            const file = input.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (e) => callback(e.target.result, { alt: file.name });
            reader.readAsDataURL(file);
          };
        },
        setup: (editor) => {
          this.editorInstance = editor;
        },
        toolbar_mode: "sliding",
        menubar: "file edit view insert format tools table tc help",
        toolbar:
          "undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat | pagebreak | charmap emoticons | fullscreen  preview save print | insertfile image media pageembed template link anchor codesample | a11ycheck ltr rtl | showcomments addcomment",
        plugins: ["lists link image paste help wordcount"],
      };
    },
  },
  methods: {
    // Namespaced DOM id: two editors (or an inline form plus a modal) must
    // never produce the same id, or <label for> and data-bs-target resolve to
    // the wrong element.
    uid(name) {
      return `examQuestions-${this.instanceId}-${name}`;
    },
    rowKey(row, index) {
      return `${index}-${row.questionType}-${row.title?.length ?? 0}`;
    },
    isEssay(question) {
      return isEssayQuestion(question);
    },
    degreeOf(question) {
      const degree = Number(question?.degree);
      return Number.isFinite(degree) ? degree : DEFAULT_QUESTION_DEGREE;
    },
    modelAnswerOf(question) {
      return question?.answers?.[0]?.title ?? "";
    },

    emitQuestions(questions) {
      this.$emit("update:questions", questions);
      this.$emit("change", questions);
    },

    // Replaces this.question with a brand new object. Touches no DOM node, so
    // it can never throw halfway through and leave the form bound to a row
    // that is already in the questions array.
    resetQuestionForm(questionType) {
      const type = questionType ?? this.question.questionType;
      this.question =
        Number(type) === QUESTION_TYPE.essay
          ? blankEssayQuestion()
          : blankQuestion(type);
      this.file = null;
      this.uploadResetKey += 1;
    },

    onQuestionTypeChanged() {
      const type = Number(this.question.questionType);
      const answers =
        type === QUESTION_TYPE.essay
          ? [{ title: this.question.answers?.[0]?.title ?? "" }]
          : blankQuestion().answers;
      this.question = {
        ...this.question,
        questionType: type,
        isRight: type === QUESTION_TYPE.essay ? null : this.question.isRight,
        answers,
      };
      this.file = null;
      this.uploadResetKey += 1;
    },

    onEditingTypeChanged() {
      const type = Number(this.editingQuestion.questionType);
      const answers =
        type === QUESTION_TYPE.essay
          ? [{ title: this.editingQuestion.answers?.[0]?.title ?? "" }]
          : [0, 1, 2, 3].map((index) => ({
              title: this.editingQuestion.answers?.[index]?.title ?? "",
            }));
      this.editingQuestion = {
        ...this.editingQuestion,
        questionType: type,
        isRight: type === QUESTION_TYPE.essay ? null : this.editingQuestion.isRight,
        answers,
      };
      this.editFile = null;
      this.editUploadResetKey += 1;
    },

    handleImages(files) {
      this.file = files[0] ?? null;
    },
    handleEditImages(files) {
      this.editFile = files[0] ?? null;
    },

    addQuestion() {
      const error = validateQuestion({ ...this.question, file: this.file });
      if (error) {
        this.showToast("error", error);
        return;
      }

      if (
        Number(this.question.questionType) === QUESTION_TYPE.image &&
        this.file
      ) {
        this.uploadThenPush();
        return;
      }

      this.pushQuestion(this.question);
    },

    uploadThenPush() {
      const content = {
        component: FirebaseUploadingFileVue,
        props: {
          title: "يتم رفع السؤال الان",
          file: this.file,
          collection: "IMAGEs",
          onFinished: (url) => {
            toast.dismiss(toastId);
            this.pushQuestion({ ...this.question, url: url ?? "" });
          },
        },
      };

      const toastId = toast(content, {
        icon: false,
        hideProgressBar: true,
        closeButton: false,
        type: TYPE.INFO,
        timeout: false,
        toastClassName: "my-custom-toast-class",
      });
    },

    // Pushes a DEEP CLONE and numbers the question. Pushing the bound object
    // itself would make the live form and the new table row the same object.
    pushQuestion(source) {
      const next = cloneQuestion(source);
      next.number = this.questions.length + 1;
      const questionType = Number(next.questionType);

      this.emitQuestions([...this.questions, next]);

      // Reset first, then confirm — so a failure can never be reported as success.
      this.resetQuestionForm(questionType);
      this.showToast("success", "تم إضافة السؤال بنجاح");
      this.focusQuestionInput();
    },

    // Puts the cursor back where the admin types the next question.
    focusQuestionInput() {
      this.$nextTick(() => {
        if (Number(this.question.questionType) === QUESTION_TYPE.image) return;
        try {
          this.editorInstance?.focus();
        } catch (error) {
          // TinyMCE not ready yet — harmless, the admin can click into it.
          console.warn("Could not focus the question editor", error);
        }
      });
    },

    openEdit(index) {
      this.editingIndex = index;
      // Clone, so typing in the modal does not mutate the table row live and
      // closing without saving really discards the change.
      this.editingQuestion = cloneQuestion(this.questions[index]);
      this.editFile = null;
      this.editUploadResetKey += 1;
    },

    cancelEdit() {
      this.editingQuestion = null;
      this.editingIndex = -1;
      this.editFile = null;
    },

    async saveEdit() {
      if (this.editingIndex < 0 || !this.editingQuestion) return;

      const error = validateQuestion({
        ...this.editingQuestion,
        file: this.editFile,
      });
      if (error) {
        this.showToast("error", error);
        return;
      }

      if (
        Number(this.editingQuestion.questionType) === QUESTION_TYPE.image &&
        this.editFile
      ) {
        const content = {
          component: FirebaseUploadingFileVue,
          props: {
            title: "يتم رفع السؤال الان",
            file: this.editFile,
            collection: "IMAGEs",
            onFinished: (url) => {
              toast.dismiss(toastId);
              this.commitEdit({ ...this.editingQuestion, url: url ?? "" });
            },
          },
        };
        const toastId = toast(content, {
          icon: false,
          hideProgressBar: true,
          closeButton: false,
          type: TYPE.INFO,
          timeout: false,
          toastClassName: "my-custom-toast-class",
        });
        return;
      }

      this.commitEdit(this.editingQuestion);
    },

    commitEdit(source) {
      const next = [...this.questions];
      next[this.editingIndex] = cloneQuestion(source);
      this.emitQuestions(next);
      this.showToast("success", "تم تعديل السؤال بنجاح");
      this.closeEditModal();
      this.cancelEdit();
    },

    closeEditModal() {
      const modal = this.$refs.editModal;
      if (!modal) return;
      const closeButton = modal.querySelector("[data-bs-dismiss='modal']");
      if (closeButton) closeButton.click();
    },

    deleteQuestion(question, index) {
      Swal.fire({
        text: "هل أنت متأكد من حذف هذا السؤال؟",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "نعم",
        cancelButtonText: "لا",
      }).then((result) => {
        if (!result.isConfirmed) return;

        if (question.url) deleteFileFromUrl(question.url);
        const next = this.questions.filter((_, itemIndex) => itemIndex !== index);
        this.emitQuestions(next);

        Swal.fire({
          text: "تم حذف السؤال بنجاح",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });
      });
    },

    showToast(icon, title) {
      Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        didOpen: (element) => {
          element.addEventListener("mouseenter", Swal.stopTimer);
          element.addEventListener("mouseleave", Swal.resumeTimer);
        },
      }).fire({ icon, title });
    },
  },
};
</script>

<style lang="scss" scoped></style>
